import { InboxRounded } from '@mui/icons-material';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AryNoContentInfoBox from 'framework/base/components/AryNoContentInfoBox';
import AryWrapper from 'framework/base/components/AryWrapper';
import useTableSelection from 'framework/base/hooks/useTableSelection';
import AryNewTableSkeleton from 'framework/desktop/AryNewTableSkeleton';
import { BodyTableCell, HeadTableCell } from 'framework/desktop/AryTableCell';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const Appending = ({ filteredColumnsList, columns }) =>
  filteredColumnsList?.some((key) => columns[key]?.appending) && (
    <TableRow>
      {filteredColumnsList?.map((key) => {
        const column = columns[key];
        const appendingSx =
          column?.appendingSx && column?.appendingSx(column.key);
        return (
          <BodyTableCell
            key={key}
            sx={{ p: 2, textAlign: 'center', ...appendingSx }}
          >
            {column.appending.renderAppending()}
          </BodyTableCell>
        );
      })}
    </TableRow>
  );

const AryTable = React.forwardRef(
  (
    {
      data,
      columns,
      filteredColumnsList,
      noContentBox,
      customNoContent,
      isFlat,
      page,
      pageCount,
      tableSx,
      onHover,
      defaultTableSelection,
      handlePopoverOpen,
      isLoading,
      skeletonSettings,
      rowBackgroundCondition,
      selectedTransactions,
      ...others
    },
    ref
  ) => {
    const { t } = useFrameworkTranslation();
    const langDirection = t('dir');
    const theme = useTheme();

    const getVisibleColumns = () => {
      if (filteredColumnsList?.length) {
        return filteredColumnsList;
      }
      return Object.keys(columns).filter(
        (columnName) => columns[columnName]?.initialVisibility !== false
      );
    };

    const tableSelection = useTableSelection(
      data,
      filteredColumnsList,
      columns,
      ref
    );
    const [clickedRowIndex, setClickedRowIndex] = useState();
    const [firstSelectedCell, setFirstSelectedCell] = useState(null);
    const stickyHeadSx = {
      position: 'sticky',
      top: 0,
      backgroundColor: theme.table.header.color,
      zIndex: 1
    };

    const handleCellMouseDown = useCallback(
      (rowIndex, columnIndex) => {
        handlePopoverOpen();
        tableSelection.setAllCellsBackgroundToTransparent();
        setFirstSelectedCell({ row: rowIndex, col: columnIndex });
        others?.handleResetStatistics();
        tableSelection.cellMouseDown(rowIndex, columnIndex);
      },
      [tableSelection, others]
    );

    const handleCellMouseEnter = useCallback(
      (rowIndex, columnIndex) => {
        tableSelection.cellMouseEnter(rowIndex, columnIndex);
      },
      [tableSelection]
    );

    const handleCellMouseUp = useCallback(
      (rowIndex, columnIndex) => {
        tableSelection.cellMouseUp();
        const newSelectedRows = [];
        for (
          let i = Math.min(firstSelectedCell.row, rowIndex);
          i <= Math.max(firstSelectedCell.row, rowIndex);
          i++
        ) {
          newSelectedRows.push(i);
        }

        const newSelectedCols = [];
        for (
          let i = Math.min(firstSelectedCell.col, columnIndex);
          i <= Math.max(firstSelectedCell.col, columnIndex);
          i++
        ) {
          newSelectedCols.push(i);
        }
        const invalidCols = [];

        newSelectedCols.forEach((col) => {
          if (!tableSelection.isColumnValid(newSelectedRows[0], col)) {
            invalidCols.push(col);
          }
        });

        if (newSelectedRows.length > 1) {
          handlePopoverOpen(
            rowIndex,
            columnIndex,
            newSelectedRows,
            newSelectedCols,
            invalidCols
          );
        }
      },
      [tableSelection, firstSelectedCell]
    );

    const handleClickOutsideTable = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        const isClickInsideMuiSelect =
          event.target.closest('.MuiMenuItem-root') || // آیتم‌های Select
          event.target.closest('.MuiSelect-root') || // خود Select
          event.target.closest('.MuiPopover-root'); // کل منوی MUI
        const isClickedInsidePopover =
          others?.popOverRef?.current &&
          others?.popOverRef?.current.contains(event.target);
        if (isClickedInsidePopover) {
          handlePopoverOpen();
        } else if (
          event.target.lastChild?.id !== 'menu-' &&
          !isClickInsideMuiSelect
        ) {
          tableSelection.setAllCellsBackgroundToTransparent();
        }
      }
    };

    useEffect(() => {
      if (ref) {
        document.addEventListener('click', handleClickOutsideTable);
      }
      return () => {
        document.removeEventListener('click', handleClickOutsideTable);
      };
    }, []);

    const renderBodyTableCell = useCallback(
      (row, key, keyIndex, itemIndex) => {
        const column = columns[key];
        const cellSx =
          column?.cellSx &&
          column?.cellSx(
            row,
            column.key,
            itemIndex,
            clickedRowIndex,
            setClickedRowIndex
          );

        return (
          <BodyTableCell
            key={key}
            id={`cell-${itemIndex}-${keyIndex}`}
            dir={langDirection}
            sx={{
              p: 0,
              textAlign: 'center',
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              ...cellSx
            }}
            onMouseDown={() => {
              if (others.isTableSelectable) {
                handleCellMouseDown(itemIndex, keyIndex);
              }
            }}
            onMouseEnter={() => {
              if (others.isTableSelectable) {
                handleCellMouseEnter(itemIndex, keyIndex);
              }
            }}
            onMouseUp={() => {
              if (others.isTableSelectable) {
                handleCellMouseUp(itemIndex, keyIndex);
              }
            }}
          >
            {column.renderCell(
              row,
              column.key,
              itemIndex,
              clickedRowIndex,
              setClickedRowIndex,
              page,
              pageCount
            ) !== undefined &&
            column.renderCell(
              row,
              column.key,
              itemIndex,
              clickedRowIndex,
              setClickedRowIndex,
              page,
              pageCount
            ) !== null
              ? column.renderCell(
                  row,
                  column.key,
                  itemIndex,
                  clickedRowIndex,
                  setClickedRowIndex,
                  page,
                  pageCount
                )
              : '-'}
          </BodyTableCell>
        );
      },
      [
        columns,
        clickedRowIndex,
        page,
        pageCount,
        handleCellMouseDown,
        handleCellMouseEnter,
        handleCellMouseUp
      ]
    );

    const memoizedRows = useMemo(() => {
      const visibleColumns = getVisibleColumns();
      return data?.map((row, itemIndex) => (
        <TableRow
          key={itemIndex}
          sx={{
            borderBottom: itemIndex === data.length - 1 && 'none',
            backgroundColor: rowBackgroundCondition?.({
              row,
              itemIndex,
              selectedTransactions,
              columns
            })
              ? 'rgba(115, 194, 251, 0.3)'
              : isFlat
              ? ''
              : itemIndex % 2 === 1
              ? theme.table.darkRow.backgroundColor
              : '',
            ...row.rowSx
          }}
          onMouseEnter={() => (onHover ? onHover(row) : undefined)}
        >
          {visibleColumns.map((key, keyIndex) =>
            renderBodyTableCell(row, key, keyIndex, itemIndex)
          )}
        </TableRow>
      ));
    }, [
      data,
      columns,
      langDirection,
      theme,
      isFlat,
      onHover,
      clickedRowIndex,
      page,
      pageCount,
      others
    ]);

    return (
      <Table
        ref={ref}
        stickyHeader
        sx={{
          overflowX: 'auto',
          height: !data?.length ? '100%' : '',
          userSelect: !defaultTableSelection && 'none',
          ...tableSx
        }}
      >
        <TableHead sx={stickyHeadSx}>
          <TableRow>
            {(() => {
              const visibleColumns = getVisibleColumns();
              return visibleColumns.map((key, itemIndex) => {
                const column = columns[key];
                const headSx =
                  column.headSx && column?.headSx(column.key, itemIndex);
                if (column) {
                  return (
                    <HeadTableCell
                      key={key}
                      sx={{
                        py: 0,
                        px: 2,
                        textAlign: 'center',
                        backgroundColor: theme.table.header.color,
                        ...headSx
                      }}
                    >
                      {column.renderHead !== undefined
                        ? column.renderHead()
                        : column.label}
                    </HeadTableCell>
                  );
                }
                return null;
              });
            })()}
          </TableRow>
        </TableHead>
        {isLoading ? (
          <AryNewTableSkeleton
            isFlat={isFlat}
            columns={columns}
            {...skeletonSettings}
          />
        ) : data?.length ||
          (() => {
            const visibleColumns = getVisibleColumns();
            return visibleColumns.some((key) => columns[key].appending);
          })() ? (
          <TableBody>
            {memoizedRows}
            <Appending
              columns={columns}
              filteredColumnsList={getVisibleColumns()}
            />
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <BodyTableCell
                colSpan={Object.keys(columns).length}
                sx={{ border: 0, height: '100%' }}
              >
                {noContentBox && (
                  <AryWrapper height="100%">
                    {customNoContent ? (
                      <AryNoContentInfoBox
                        text={customNoContent.text}
                        icon={customNoContent.icon}
                        height={customNoContent.height}
                        textVariant={customNoContent.textVariant}
                        buttonProps={customNoContent.buttonProps}
                        minHeight={customNoContent.minHeight}
                      />
                    ) : (
                      <AryNoContentInfoBox
                        height="100%"
                        text={t('No Content Message')}
                        icon={InboxRounded}
                        textVariant="h3Medium"
                      />
                    )}
                  </AryWrapper>
                )}
              </BodyTableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    );
  }
);

export default AryTable;
